import type { VoidFunctionComponent } from 'react';

import LabeledTextArea from '../../generic/Forms/LabeledTextArea';
import Markdown from '../../generic/Markdown';
import Tabs from '../../generic/Tabs';
import useTabs from '../../generic/Tabs/useTabs';
import Section from '../../shared/settings/SettingsPageSection';

import styles from './Description.module.less';

type Props = {
  value: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  isReleaseGroup?: boolean;
};

const TAB_IDS = ['Edit', 'Preview'] as const;

/**
 * Shared description editor for report settings pages.
 * Lives in the top-level reportsv3 directory because it is used by both
 * the project and release group settings pages.
 */
export const Description: VoidFunctionComponent<Props> = ({
  value, onChange, isDisabled = false, isReleaseGroup = false,
}) => {
  const { tabs, activeTab } = useTabs({ tabIds: TAB_IDS });

  return (
    <Section>
      <Section.Left heading="Description">
        <Section.Paragraph>
          Add a short summary or disclaimer that will appear on every report in this {isReleaseGroup ? 'release group' : 'project'}.
        </Section.Paragraph>
      </Section.Left>
      <Section.Right>
        <Tabs>
          {tabs.map((tab) => (
            <Tabs.Tab
              key={tab.label}
              label={tab.label}
              isActive={tab.isActive}
              onSelect={tab.onSelect}
            />
          ))}
        </Tabs>
        {activeTab === 'Edit' && (
          <LabeledTextArea
            label="Uses Markdown Format"
            value={value}
            onChange={(val) => onChange(val)}
            isDisabled={isDisabled}
          />
        )}
        {activeTab === 'Preview' && (
          value
            ? <Markdown text={value} />
            : <p className={styles.emptyPreview}>Nothing to preview</p>
        )}
      </Section.Right>
    </Section>
  );
};
